import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Users, Copy, Check, Share2, Eye, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function Family() {
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [members, setMembers] = useState({ shared_with_me: [], my_shares: [] });
  const [joinCode, setJoinCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => setMembers((await api.get("/family/members")).data);
  useEffect(() => { load(); }, []);

  const isPremiumPlus = user?.tier === "premium_plus";

  const createInvite = async () => {
    setLoading(true);
    try { const r = await api.post("/family/invite"); setInvite(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const accept = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/accept", { code: joinCode.toUpperCase() });
      toast.success("You now have shared view access");
      setJoinCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
  };

  const copy = async () => {
    const url = `${window.location.origin}/family?join=${invite.code}`;
    await navigator.clipboard.writeText(`${invite.code}\n${url}`);
    setCopied(true); toast.success("Invite copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-6 h-6 text-[color:var(--gold)]" />
        <h1 className="font-heading text-3xl font-bold">Family Money</h1>
      </div>
      <p className="text-[color:var(--text-secondary)] mb-8">Share a read-only view of your money with a partner or family member.</p>

      {!isPremiumPlus && (
        <div className="glass-card p-8 mb-6 border-[rgba(212,175,55,0.35)]">
          <div className="flex items-center gap-3 mb-3">
            <Crown className="w-5 h-5 text-[color:var(--gold)]" />
            <h2 className="font-heading text-xl font-semibold">Premium+ feature</h2>
          </div>
          <p className="text-sm text-[color:var(--text-secondary)] mb-4">Family sharing is part of Premium+. Upgrade to invite a partner or family member into your money view.</p>
          <Link to="/settings" className="gold-btn inline-flex px-5 py-2.5 rounded-full">Upgrade to Premium+</Link>
        </div>
      )}

      {isPremiumPlus && (
        <>
          <div className="glass-card p-6 mb-6">
            <h2 className="font-heading text-lg font-semibold mb-3">Invite someone into your view</h2>
            {!invite ? (
              <button data-testid="create-invite-btn" onClick={createInvite} disabled={loading} className="gold-btn px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Create invite code
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <div data-testid="invite-code" className="font-mono text-3xl gold-gradient-text font-bold">{invite.code}</div>
                <button data-testid="copy-invite-btn" onClick={copy} className="gold-btn px-4 py-2 rounded-full text-sm inline-flex items-center gap-2">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy invite
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <div className="glass-card p-6 mb-6">
        <h2 className="font-heading text-lg font-semibold mb-3">Join with a code</h2>
        <p className="text-xs text-[color:var(--text-secondary)] mb-3">Have an invite code from a family member? Paste it below.</p>
        <form onSubmit={accept} className="flex gap-2">
          <input data-testid="join-code-input" required placeholder="FAM-XXXXXX" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
            className="flex-1 px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none font-mono uppercase" />
          <button data-testid="join-submit-btn" type="submit" className="gold-btn px-5 py-3 rounded-xl">Accept</button>
        </form>
      </div>

      {members.shared_with_me.length > 0 && (
        <div className="mb-6">
          <h2 className="font-heading text-lg font-semibold mb-3">Shared with you</h2>
          <div className="space-y-2">
            {members.shared_with_me.map((m, i) => (
              <div key={i} className="glass-card p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center font-semibold text-[color:var(--gold)]">
                  {m.owner.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{m.owner.name}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">{m.owner.email}</div>
                </div>
                <Link to={`/family/view/${m.owner_id}`} data-testid={`view-${m.owner_id}`}
                  className="px-4 py-2 rounded-full border border-[rgba(212,175,55,0.25)] hover:border-[color:var(--gold)] text-xs inline-flex items-center gap-2 transition">
                  <Eye className="w-3 h-3" /> View money
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {members.my_shares.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold mb-3">Sharing with</h2>
          <div className="space-y-2">
            {members.my_shares.map((m, i) => (
              <div key={i} className="glass-card p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[rgba(42,157,143,0.15)] flex items-center justify-center font-semibold text-[color:var(--emerald)]">
                  {m.viewer.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{m.viewer.name}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">{m.viewer.email} · read-only</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
